import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, Lock, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ResetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({ new_password: '', confirm: '' });

  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Lien invalide');
      return;
    }
    if (!form.new_password || form.new_password.length < 6) {
      toast.error('Mot de passe trop court (min. 6 caractères)');
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: form.new_password,
      });
      setSuccess(true);
      toast.success('Mot de passe réinitialisé');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-6" data-testid="reset-password-page">
      <Card className="w-full max-w-md glass-card shadow-xl animate-fade-in">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Calendar className="w-8 h-8 text-[#065F46]" />
            <span className="font-outfit font-bold text-2xl tracking-tight text-[#065F46]">Ledgerly</span>
          </div>
          <CardTitle className="font-outfit text-xl text-slate-900">
            {success ? 'Mot de passe modifié' : 'Nouveau mot de passe'}
          </CardTitle>
          <CardDescription className="text-slate-500">
            {success ? 'Vous pouvez maintenant vous connecter.' : 'Choisissez un nouveau mot de passe pour votre compte.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <Button
                className="w-full h-11 bg-[#065F46] hover:bg-[#047857] text-white font-medium"
                onClick={() => { window.location.href = '/login'; }}
                data-testid="back-to-login-btn"
              >
                Aller à la connexion
              </Button>
            </div>
          ) : !token ? (
            <p className="text-center text-sm text-red-600">Lien invalide. Demandez un nouveau lien depuis la page de connexion.</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="rp-new" className="text-slate-700">Nouveau mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="rp-new"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-11"
                    value={form.new_password}
                    onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                    data-testid="rp-new-password-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rp-confirm" className="text-slate-700">Confirmer le mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="rp-confirm"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-11"
                    value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    data-testid="rp-confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-[#065F46] hover:bg-[#047857] text-white font-medium"
                disabled={loading}
                data-testid="rp-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Réinitialiser'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
